import type { DatasourceEntry } from '~/types/DatasourceEntry';
import { reuseSsrPayloadOnServer } from '~/utils/reuseSsrPayload';

interface Options {
    /** If set to true, errors during the fetch will not throw a fatal error. */
    disableFatalErrors?: boolean;
}

/**
 * Fetches entries from a specified Storyblok datasource.
 *
 * For fetching any single arbitrary story, use `useStory`. For fetching the current config, use `useConfig`. For
 * fetching the global config, use `useGlobalConfig`. For fetching multiple stories, use `useStories`.
 *
 * **Example:**
 * ```ts
 * import { useDatasourceEntries } from '~/composables/storyblok-proxy/useDatasourceEntries';
 *
 * const { data: datasourceData } = await useDatasourceEntries('news-categories');
 * ```
 *
 * @param slug - The slug of the datasource to fetch entries from.
 * @param options - Optional settings. See {@link Options} for more details.
 * @returns A reactive object containing the fetched datasource entries, loading state, and error state.
 * @throws Will throw an fatal error if the fetch fails.
 */
export const useDatasourceEntries = (slug: Ref<string> | string, options?: Options) => {
    const slugRef = toRef(slug);
    const { lang } = useLanguage();
    const stringifiedParams = computed(() =>
        JSON.stringify({
            datasource: slugRef.value,
            dimension: lang.value ?? undefined,
            cv: 0
        })
    );
    const headers = useRequestHeaders(['cookie']);

    const result = useFetch<DatasourceEntry[]>(StoryblokProxyEndpoint, {
        query: {
            type: 'datasource_entries',
            params: stringifiedParams
        },
        headers,
        // `dedupe` only joins calls that overlap in time; `getCachedData` is what
        // makes a second caller later in the same render reuse the first response.
        dedupe: 'defer',
        getCachedData: reuseSsrPayloadOnServer
    });

    watch(result.error, (error) => {
        if (!error) return;
        if (options?.disableFatalErrors) return;

        throw createError({
            statusCode: error.statusCode,
            message: error.message,
            fatal: true
        });
    });

    return result;
};
